import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DelKnapp } from "./DelKnapp";
import { Scale, ExternalLink, Info, Baby, Trophy, TrendingUp, Coins, Map } from "lucide-react";
import {
  ComposedChart, Bar, Line, BarChart, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell, ScatterChart, Scatter,
} from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, dec, num } from "@/lib/formatters";
import { tooltipStyle } from "./oversiktConstants";

const g = (v) => (v == null ? "—" : dec(v, 3));
const p = (v) => (v == null ? "—" : `${dec(v, 1)} %`);
const kr = (v) => (v == null ? "—" : `${formatNumber(v)} kr`);

export const UlikskapSection = ({ data, kommuneName, kommuneCode }) => {
  const [budsjettModus, setBudsjettModus] = useState("innb");
  const gini = data?.gini;
  if (!gini?.latest) return null;
  const last = gini.latest;
  const first = gini.first;
  const p9 = data.p90p10?.latest;
  const desil = data.desil;
  const storby = !!data.gruppe_namn;
  const gruppeOrd = storby ? "storbygruppa" : "fylket";
  const fylkeKey = storby ? "Snitt for storbyane" : `Fylkessnitt (${data.fylke_name || "fylket"})`;
  const utanFylke = !!data.eige_fylke;
  const giniChart = (gini.history || []).map((h) => ({
    year: h.year,
    Kommunen: h.kommune,
    ...(utanFylke ? {} : { [fylkeKey]: h.fylke }),
    Landet: h.land,
  }));
  const desilChart = (desil?.rows || []).map((r) => ({
    name: `${r.desil}`,
    Andel: r.andel_pct,
    _avvik: r.avvik_pp,
    _fra: r.fra_kr,
    _til: r.til_kr,
  }));
  const vsLand = last.land != null ? last.kommune - last.land : null;
  const barn = data.barnefattigdom;
  const barnLast = barn?.latest;
  const barnChart = (barn?.history || []).map((h) => ({
    period: h.period,
    Kommunen: h.kommune,
    ...(utanFylke ? {} : { [fylkeKey]: h.fylke }),
    Landet: h.land,
    _personar: h.personar,
  }));
  const fylkeRang = data.gini_fylke;
  const barnRang = data.barn_fylke;
  const budsjett = data.barn_budsjett;
  const perBarn = budsjettModus === "barn";
  const krFelt = perBarn ? "kr_per_barn" : "kr_per_innb";
  const korr = perBarn ? budsjett?.korrelasjon_barn : budsjett?.korrelasjon;
  const mest = perBarn ? budsjett?.mest_per_barn : budsjett?.mest_per_innb;
  const minst = perBarn ? budsjett?.minst_per_barn : budsjett?.minst_per_innb;
  const land = data.land_rangering;
  const budsjettChart = (budsjett?.rows || []).map((r) => ({
    kommune_code: r.kommune_code,
    name: r.name,
    andel: r.andel,
    kr: r.kr_per_innb,
    krBarn: r.kr_per_barn,
    barnehage: r.kr_barnehage,
    grunnskole: r.kr_grunnskole,
    barnevern: r.kr_barnevern,
    barnehageBarn: r.kr_barn_barnehage,
    grunnskoleBarn: r.kr_barn_grunnskole,
    barnevernBarn: r.kr_barn_barnevern,
    self: r.kommune_code === kommuneCode,
  }));
  const overTid = data.desil_over_tid;
  const overTidChart = (overTid?.rows || []).map((r) => ({
    year: r.year,
    "Lågaste tre grupper": r.lav_pct,
    "Midtre fire grupper": r.midt_pct,
    "Høgaste tre grupper": r.hog_pct,
  }));
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="ulikskap-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
            <Scale className="w-5 h-5 text-[#103766]" />
            Inntektsulikskap og inntektsgrupper {last.year}
          </CardTitle>
          {kommuneCode && <DelKnapp kommuneCode={kommuneCode} kommuneNamn={kommuneName} tema="ulikskap" testId="del-ulikskap" />}
        </div>
        <p className="text-sm text-[#4B647F]">
          Kor jamt inntekta etter skatt er fordelt mellom hushalda i {kommuneName}, og kor mange hushald som ligg i kvar inntektsgruppe i landet.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="gini-kommune">
            <p className="text-xs text-[#8AA4BF]">Gini-koeffisient</p>
            <p className="text-2xl font-light text-[#1F2A36]">{g(last.kommune)}</p>
            <p className="text-[11px] text-[#4B647F]">{first?.year}: {g(first?.kommune)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="gini-snitt">
            <p className="text-xs text-[#8AA4BF]">{utanFylke ? "Landssnitt" : "Fylke og land"}</p>
            <p className="text-2xl font-light text-[#3F8A86]">{g(utanFylke ? last.land : last.fylke)}</p>
            <p className="text-[11px] text-[#4B647F]">{utanFylke ? "Oslo er både kommune og fylke" : `Landet: ${g(last.land)}`}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="p90p10-kommune">
            <p className="text-xs text-[#8AA4BF]">P90/P10</p>
            <p className="text-2xl font-light text-[#1F2A36]">{g(p9?.kommune)}</p>
            <p className="text-[11px] text-[#4B647F]">Landet: {g(p9?.land)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="barnefattigdom-flis">
            <p className="text-xs text-[#8AA4BF] flex items-center gap-1"><Baby className="w-3.5 h-3.5" /> Barn i vedvarande låginntekt</p>
            <p className={`text-2xl font-light ${barnLast && barnLast.land != null && barnLast.kommune > barnLast.land ? "text-[#C0392B]" : "text-[#1F2A36]"}`}>
              {p(barnLast?.kommune)}
            </p>
            <p className="text-[11px] text-[#4B647F]">
              {barnLast?.personar ? `${formatNumber(barnLast.personar)} barn · ` : ""}landet {p(barnLast?.land)}
            </p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="desil-fordeling">
            <p className="text-xs text-[#8AA4BF]">Låg- og høginntektshushald</p>
            <p className="text-2xl font-light text-[#1F2A36]">{p(desil?.lav_pct)}</p>
            <p className="text-[11px] text-[#4B647F]">i dei tre lågaste gruppene · {p(desil?.hog_pct)} i dei tre høgaste</p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 border border-[#DCE6F2] bg-[#F5F8FC] rounded-lg" data-testid="gini-forklaring">
          <Info className="w-5 h-5 text-[#103766] mt-0.5 shrink-0" />
          <div className="space-y-1 text-sm text-[#4B647F]">
            <p>
              <span className="font-medium text-[#1F2A36]">Gini-koeffisienten</span> måler kor skeivt inntekta er fordelt: <span className="font-medium text-[#1F2A36]">0</span> tyder at alle hushald har like mykje å leve av etter skatt, <span className="font-medium text-[#1F2A36]">1</span> at eitt hushald har all inntekta. Norske kommunar ligg typisk mellom 0,20 og 0,30 – og eit lågare tal enn landet tyder at skilnadene i kommunen er mindre enn elles i landet.
            </p>
            <p>
              <span className="font-medium text-[#1F2A36]">P90/P10</span> viser kor mange gonger meir hushaldet på 90. persentil har enn hushaldet på 10. persentil. Begge måla byggjer på inntekt etter skatt per forbrukseining, så store hushald blir samanliknbare med små.
            </p>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">Gini-koeffisient over tid – mot {utanFylke ? "landssnittet" : "fylkes- og landssnitt"}</p>
          {!utanFylke && (
            <p className="text-xs text-[#8AA4BF] mb-2">Fylkeslinja har brot i 2020–2023 fordi kommunen då høyrde til eit anna fylke etter regionreforma.</p>
          )}
          <div className="h-[280px]" data-testid="gini-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={giniChart}>
                {endringsLinjer(giniChart)}
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                <YAxis stroke="#8AA4BF" fontSize={12} width={55} domain={["auto", "auto"]} tickFormatter={(v) => num(v, 3)} />
                <Tooltip formatter={(v) => g(v)} contentStyle={tooltipStyle} />
                <Legend itemSorter={null} wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Kommunen" stroke="#103766" strokeWidth={3} dot={{ r: 3 }} connectNulls />
                {!utanFylke && <Line type="monotone" dataKey={fylkeKey} stroke="#3F8A86" strokeWidth={2} dot={{ r: 2.5 }} connectNulls={false} />}
                <Line type="monotone" dataKey="Landet" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {desil && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
              Inntektsgrupper {desil.year} – kor mange prosent av hushalda som ligg i kvar tidels-gruppe i landet
            </p>
            <p className="text-xs text-[#8AA4BF] mb-2">
              Gruppe 1 er dei 10 % av hushalda i landet med lågast inntekt etter skatt, gruppe 10 dei 10 % med høgast. Den stipla linja på 10 % er ei heilt gjennomsnittleg kommune.
            </p>
            <div className="h-[280px]" data-testid="desil-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={desilChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} label={{ value: "Inntektsgruppe (1 = lågast)", position: "insideBottom", offset: -4, fontSize: 11, fill: "#8AA4BF" }} />
                  <YAxis stroke="#8AA4BF" fontSize={12} width={50} tickFormatter={(v) => `${num(v)} %`} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v, _n, pl) => [
                      `${dec(v, 1)} % av hushalda (${pl.payload._avvik >= 0 ? "+" : "−"}${dec(Math.abs(pl.payload._avvik), 1)} pp mot landet)`,
                      pl.payload._til ? `${kr(pl.payload._fra)}–${kr(pl.payload._til)}` : `over ${kr(pl.payload._fra)}`,
                    ]}
                  />
                  <ReferenceLine y={10} stroke="#C0392B" strokeDasharray="4 3" label={{ value: "10 % = landssnitt", position: "right", fontSize: 10, fill: "#C0392B" }} />
                  <Bar dataKey="Andel" radius={[4, 4, 0, 0]}>
                    {desilChart.map((d) => (
                      <Cell key={d.name} fill={d._avvik >= 0 ? "#103766" : "#9FB6CC"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs" data-testid="desil-table">
                <thead>
                  <tr className="border-b border-[#E6E9F0] text-[#4B647F]">
                    <th className="py-2 pr-3 text-left font-medium">Inntektsgruppe</th>
                    <th className="py-2 px-3 text-left font-medium">Inntekt etter skatt (heile landet)</th>
                    <th className="py-2 px-3 text-right font-medium">Andel av hushalda</th>
                    <th className="py-2 pl-3 text-right font-medium">Mot landet</th>
                  </tr>
                </thead>
                <tbody>
                  {desil.rows.map((r) => (
                    <tr key={r.desil} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`desil-row-${r.desil}`}>
                      <td className="py-2 pr-3">Gruppe {r.desil}{r.desil === 1 ? " (lågast)" : r.desil === 10 ? " (høgast)" : ""}</td>
                      <td className="py-2 px-3 text-[#4B647F]">{r.til_kr ? `${kr(r.fra_kr)} – ${kr(r.til_kr)}` : `over ${kr(r.fra_kr)}`}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{p(r.andel_pct)}</td>
                      <td className={`py-2 pl-3 text-right tabular-nums ${r.avvik_pp > 0 ? "text-[#103766]" : r.avvik_pp < 0 ? "text-[#C0392B]" : "text-[#4B647F]"}`}>
                        {r.avvik_pp == null ? "—" : `${r.avvik_pp > 0 ? "+" : r.avvik_pp < 0 ? "−" : ""}${dec(Math.abs(r.avvik_pp), 1)} pp`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {barnChart.length > 0 && (
          <div data-testid="barnefattigdom-blokk">
            <div className="flex items-start justify-between gap-3 mb-1">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">
                Barn i hushald med vedvarande låginntekt – {barn.first?.period} til {barnLast?.period}
              </p>
            </div>
            <p className="text-xs text-[#8AA4BF] mb-2">
              Andel barn 0–17 år som bur i hushald med inntekt under 60 % av medianen i heile treårsperioden. Dette er det vanlegaste målet på barnefattigdom.
            </p>
            <div className="h-[260px]" data-testid="barnefattigdom-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={barnChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="period" stroke="#8AA4BF" fontSize={10} angle={-25} textAnchor="end" height={54} />
                  <YAxis stroke="#8AA4BF" fontSize={11} width={48} tickFormatter={(v) => `${num(v)} %`} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v, n, pl) => [n === "Kommunen" && pl.payload._personar ? `${dec(v, 1)} % (${formatNumber(pl.payload._personar)} barn)` : `${dec(v, 1)} %`, n]}
                  />
                  <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Kommunen" fill="#D35E4E" radius={[4, 4, 0, 0]} />
                  {!utanFylke && <Line type="monotone" dataKey={fylkeKey} stroke="#3F8A86" strokeWidth={2} dot={{ r: 2.5 }} connectNulls={false} />}
                  <Line type="monotone" dataKey="Landet" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {barnRang && (
          <div data-testid="barn-fylke-rangering">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1 flex items-center gap-1">
              <Baby className="w-3.5 h-3.5" /> Barnefattigdom i {storby ? "storbygruppa" : data.fylke_name || "fylket"} {barnRang.period}
            </p>
            <p className="text-xs text-[#8AA4BF] mb-3">
              Flest: <span className="font-medium text-[#1F2A36]">{barnRang.flest.name}</span> ({p(barnRang.flest.andel)}) · Færrast:{" "}
              <span className="font-medium text-[#1F2A36]">{barnRang.faerrast.name}</span> ({p(barnRang.faerrast.andel)}) · {kommuneName} er nr.{" "}
              <span className="font-medium text-[#1F2A36]">{barnRang.own_rank}</span> av {barnRang.count} ({storby ? "storbysnitt" : "fylkessnitt"} {p(barnRang.fylke_snitt)}
              {barnRang.sum_personar ? ` · ${formatNumber(barnRang.sum_personar)} barn i ${storby ? "dei fire byane" : "heile fylket"}` : ""})
            </p>
            <div className="space-y-1">
              {barnRang.rows.map((r) => {
                const self = r.kommune_code === kommuneCode;
                const max = Math.max(...barnRang.rows.map((x) => x.andel));
                return (
                  <div key={r.kommune_code} className="flex items-center gap-2 text-xs" data-testid={`barn-fylke-${r.kommune_code}`}>
                    <span className="w-6 text-right text-[#8AA4BF]">{r.rank}.</span>
                    <span className={`w-32 truncate ${self ? "font-semibold text-[#103766]" : "text-[#4B647F]"}`}>{r.name}</span>
                    <span className="flex-1 h-2.5 bg-[#F0F3F8] rounded-full overflow-hidden">
                      <span className={`block h-full rounded-full ${self ? "bg-[#C0392B]" : "bg-[#E3B0A8]"}`} style={{ width: `${(r.andel / max) * 100}%` }} />
                    </span>
                    <span className="w-24 text-right text-[#8AA4BF] tabular-nums hidden sm:block">
                      {r.personar != null ? `${formatNumber(r.personar)} barn` : ""}
                    </span>
                    <span className={`w-14 text-right tabular-nums ${self ? "font-semibold text-[#103766]" : "text-[#4B647F]"}`}>{p(r.andel)}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[#8AA4BF] mt-2">Sortert frå flest til færrast barn i hushald med vedvarande låginntekt.</p>
          </div>
        )}

        {budsjett && (
          <div data-testid="barn-budsjett">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1 flex items-center gap-1">
              <Coins className="w-3.5 h-3.5" /> Barnefattigdom mot pengebruk på barn og unge {budsjett.year}
            </p>
            <p className="text-xs text-[#8AA4BF] mb-2">
              Kvart punkt er ein kommune i {storby ? "storbygruppa" : data.fylke_name || "fylket"}: barnefattigdom {budsjett.period} vassrett, og netto driftsutgifter til
              barnehage, grunnskule og barnevern {perBarn ? "per barn 0–17 år" : "per innbyggjar"} loddrett.{" "}
              {korr == null ? "" : korr <= -0.2
                ? `I ${gruppeOrd} brukar kommunane med mest barnefattigdom minst på barn og unge (samanheng ${g(korr)}).`
                : korr >= 0.2
                  ? `I ${gruppeOrd} brukar kommunane med mest barnefattigdom også mest på barn og unge (samanheng ${g(korr)}).`
                  : `I ${gruppeOrd} er det ingen tydeleg samanheng mellom barnefattigdom og pengebruk (samanheng ${g(korr)}).`}
            </p>
            <div className="inline-flex p-0.5 bg-[#F0F3F8] rounded-full mb-3" data-testid="barn-budsjett-modus">
              {[
                { key: "innb", label: "Per innbyggjar" },
                { key: "barn", label: "Per barn 0–17 år" },
              ].map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setBudsjettModus(o.key)}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    budsjettModus === o.key ? "bg-white text-[#103766] font-medium shadow-sm" : "text-[#4B647F] hover:text-[#1F2A36]"
                  }`}
                  data-testid={`barn-budsjett-modus-${o.key}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="barn-budsjett-kommune">
                <p className="text-xs text-[#8AA4BF]">{kommuneName} brukar</p>
                <p className="text-2xl font-light text-[#1F2A36]">{kr(perBarn ? budsjett.own?.kr_per_barn : budsjett.own?.kr_per_innb)}</p>
                <p className="text-[11px] text-[#4B647F]">
                  {perBarn
                    ? `per barn 0–17 år${budsjett.own?.barn_0_17 ? ` · ${formatNumber(budsjett.own.barn_0_17)} barn` : ""}`
                    : "per innbyggjar på barn og unge"}
                </p>
              </div>
              <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="barn-budsjett-fylke">
                <p className="text-xs text-[#8AA4BF]">{storby ? "Storbysnitt" : "Fylkessnitt"}</p>
                <p className="text-2xl font-light text-[#3F8A86]">{kr(perBarn ? budsjett.fylke_snitt_kr_barn : budsjett.fylke_snitt_kr)}</p>
                <p className="text-[11px] text-[#4B647F]">barnefattigdom {p(budsjett.fylke_snitt_andel)}</p>
              </div>
              <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="barn-budsjett-mest">
                <p className="text-xs text-[#8AA4BF]">Brukar mest</p>
                <p className="text-lg font-medium text-[#1F2A36] truncate">{mest?.name || "—"}</p>
                <p className="text-[11px] text-[#4B647F]">{kr(mest?.[krFelt])} · {p(mest?.andel)} fattigdom</p>
              </div>
              <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="barn-budsjett-minst">
                <p className="text-xs text-[#8AA4BF]">Brukar minst</p>
                <p className="text-lg font-medium text-[#1F2A36] truncate">{minst?.name || "—"}</p>
                <p className="text-[11px] text-[#4B647F]">{kr(minst?.[krFelt])} · {p(minst?.andel)} fattigdom</p>
              </div>
            </div>
            <div className="h-[300px]" data-testid="barn-budsjett-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 20, bottom: 24, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis
                    type="number" dataKey="andel" stroke="#8AA4BF" fontSize={11} domain={["dataMin - 1", "dataMax + 1"]}
                    tickFormatter={(v) => `${num(v, 1)} %`}
                    label={{ value: "Barn i vedvarande låginntekt", position: "insideBottom", offset: -14, fontSize: 11, fill: "#8AA4BF" }}
                  />
                  <YAxis
                    type="number" dataKey={perBarn ? "krBarn" : "kr"} stroke="#8AA4BF" fontSize={11} width={78}
                    domain={perBarn ? ["dataMin - 10000", "dataMax + 10000"] : ["dataMin - 2000", "dataMax + 2000"]}
                    tickFormatter={(v) => formatNumber(v)}
                  />
                  <ZAxis range={[90, 90]} />
                  <ReferenceLine x={budsjett.fylke_snitt_andel} stroke="#C0392B" strokeDasharray="4 3" />
                  <ReferenceLine y={perBarn ? budsjett.fylke_snitt_kr_barn : budsjett.fylke_snitt_kr} stroke="#3F8A86" strokeDasharray="4 3" />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v, n, pl) =>
                      n === "andel"
                        ? [p(v), "Barnefattigdom"]
                        : [
                            `${kr(v)} ${perBarn ? "per barn" : "per innbyggjar"} (barnehage ${kr(perBarn ? pl.payload.barnehageBarn : pl.payload.barnehage)}, skule ${kr(perBarn ? pl.payload.grunnskoleBarn : pl.payload.grunnskole)}, barnevern ${kr(perBarn ? pl.payload.barnevernBarn : pl.payload.barnevern)})`,
                            pl.payload.name,
                          ]
                    }
                  />
                  <Scatter data={budsjettChart}>
                    {budsjettChart.map((d) => (
                      <Cell key={d.kommune_code} fill={d.self ? "#103766" : "#9FB6CC"} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs" data-testid="barn-budsjett-table">
                <thead>
                  <tr className="border-b border-[#E6E9F0] text-[#4B647F]">
                    <th className="py-2 pr-3 text-left font-medium">Kommune</th>
                    <th className="py-2 px-3 text-right font-medium">Barnefattigdom</th>
                    <th className="py-2 px-3 text-right font-medium">Barnehage</th>
                    <th className="py-2 px-3 text-right font-medium">Grunnskule</th>
                    <th className="py-2 px-3 text-right font-medium">Barnevern</th>
                    <th className="py-2 pl-3 text-right font-medium">{perBarn ? "Sum per barn 0–17 år" : "Sum per innbyggjar"}</th>
                  </tr>
                </thead>
                <tbody>
                  {budsjett.rows.map((r) => (
                    <tr
                      key={r.kommune_code}
                      className={`border-b border-[#F1F4F8] ${r.kommune_code === kommuneCode ? "bg-[#F5F8FC] font-semibold text-[#103766]" : "text-[#1F2A36]"}`}
                      data-testid={`barn-budsjett-row-${r.kommune_code}`}
                    >
                      <td className="py-2 pr-3">{r.name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{p(r.andel)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{kr(perBarn ? r.kr_barn_barnehage : r.kr_barnehage)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{kr(perBarn ? r.kr_barn_grunnskole : r.kr_grunnskole)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{kr(perBarn ? r.kr_barn_barnevern : r.kr_barnevern)}</td>
                      <td className="py-2 pl-3 text-right tabular-nums">{kr(r[krFelt])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#8AA4BF] mt-2">
              {perBarn
                ? "Per barn 0–17 år er netto driftsutgifter til barnehage, grunnskule og barnevern delt på talet på barn i kommunen (SSB 07459), slik at små og store kommunar blir rettferdig samanlikna uavhengig av kor mange barn dei har."
                : "Per innbyggjar er netto driftsutgifter delt på heile folketalet (KOSTRA-tabell 12362) – nyttig for å sjå kor stor del av kommunekassa som går til barn og unge."}
            </p>
          </div>
        )}

        {fylkeRang && (
          <div data-testid="gini-fylke-rangering">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1 flex items-center gap-1">
              <Trophy className="w-3.5 h-3.5" /> Inntektsfordeling i {storby ? "storbygruppa" : data.fylke_name || "fylket"} {fylkeRang.year}
            </p>
            <p className="text-xs text-[#8AA4BF] mb-3">
              Jamnast: <span className="font-medium text-[#1F2A36]">{fylkeRang.jamnast.name}</span> ({g(fylkeRang.jamnast.gini)}) · Skeivast:{" "}
              <span className="font-medium text-[#1F2A36]">{fylkeRang.skeivast.name}</span> ({g(fylkeRang.skeivast.gini)}) · {kommuneName} er nr.{" "}
              <span className="font-medium text-[#1F2A36]">{fylkeRang.own_rank}</span> av {fylkeRang.count} ({storby ? "storbysnitt" : "fylkessnitt"} {g(fylkeRang.fylke_snitt)})
            </p>
            <div className="space-y-1">
              {fylkeRang.rows.map((r) => {
                const self = r.kommune_code === fylkeRang.rows.find((x) => x.rank === fylkeRang.own_rank)?.kommune_code;
                const max = Math.max(...fylkeRang.rows.map((x) => x.gini));
                return (
                  <div key={r.kommune_code} className="flex items-center gap-2 text-xs" data-testid={`gini-fylke-${r.kommune_code}`}>
                    <span className="w-6 text-right text-[#8AA4BF]">{r.rank}.</span>
                    <span className={`w-32 truncate ${self ? "font-semibold text-[#103766]" : "text-[#4B647F]"}`}>{r.name}</span>
                    <span className="flex-1 h-2.5 bg-[#F0F3F8] rounded-full overflow-hidden">
                      <span className={`block h-full rounded-full ${self ? "bg-[#103766]" : "bg-[#9FB6CC]"}`} style={{ width: `${(r.gini / max) * 100}%` }} />
                    </span>
                    <span className={`w-14 text-right tabular-nums ${self ? "font-semibold text-[#103766]" : "text-[#4B647F]"}`}>{g(r.gini)}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[#8AA4BF] mt-2">Sortert frå jamnast (lågast Gini) til skeivast fordeling.</p>
          </div>
        )}

        {overTid && (
          <div data-testid="desil-over-tid">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" /> Inntektsgruppene over tid {overTid.first_year}–{overTid.last_year}
            </p>
            <p className="text-xs text-[#8AA4BF] mb-2">
              Dei lågaste tre gruppene har{" "}
              <span className="font-medium text-[#1F2A36]">
                {overTid.endring_lav_pp > 0 ? "vakse" : overTid.endring_lav_pp < 0 ? "krympa" : "halde seg"}{" "}
                {overTid.endring_lav_pp != null ? `${dec(Math.abs(overTid.endring_lav_pp), 1)} prosentpoeng` : ""}
              </span>{" "}
              sidan {overTid.first_year}, mens dei høgaste tre har{" "}
              <span className="font-medium text-[#1F2A36]">
                {overTid.endring_hog_pp > 0 ? "vakse" : overTid.endring_hog_pp < 0 ? "krympa" : "halde seg"}{" "}
                {overTid.endring_hog_pp != null ? `${dec(Math.abs(overTid.endring_hog_pp), 1)} prosentpoeng` : ""}
              </span>
              .
            </p>
            <div className="h-[260px]" data-testid="desil-over-tid-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={overTidChart}>
                  {endringsLinjer(overTidChart)}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                  <YAxis stroke="#8AA4BF" fontSize={11} width={48} tickFormatter={(v) => `${num(v)} %`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${dec(v, 1)} %`} />
                  <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="Lågaste tre grupper" stroke="#C0392B" strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls />
                  <Line type="monotone" dataKey="Midtre fire grupper" stroke="#9FB6CC" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                  <Line type="monotone" dataKey="Høgaste tre grupper" stroke="#2F7D5B" strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {land?.barnefattigdom && (
          <div data-testid="land-rangering">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1 flex items-center gap-1">
              <Map className="w-3.5 h-3.5" /> Kvar ligg {kommuneName} i heile landet?
            </p>
            <p className="text-xs text-[#8AA4BF] mb-3">
              Plasseringa blant alle kommunane med tal, ikkje berre i eige fylke. Nr. 1 har høgast verdi.
              {land.barnefattigdom.percentil != null
                ? ` På barnefattigdom er ${kommuneName} blant dei ${land.barnefattigdom.percentil} % av kommunane med mest barnefattigdom (medianen i landet er ${p(land.barnefattigdom.median)}).`
                : ""}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                { key: "barnefattigdom", label: `Barnefattigdom ${land.period}`, fmt: p, note: "av barna 0–17 år", testId: "land-barnefattigdom" },
                { key: "kr_per_barn", label: `Brukt per barn ${land.year}`, fmt: kr, note: "barnehage, skule og barnevern", testId: "land-kr-per-barn" },
                { key: "kr_per_innb", label: `Brukt per innbyggjar ${land.year}`, fmt: kr, note: "på barn og unge", testId: "land-kr-per-innb" },
                { key: "gini", label: `Gini ${land.gini_year}`, fmt: g, note: "nr. 1 = mest ulikt", testId: "land-gini" },
              ].map((m) => {
                const r = land[m.key];
                return (
                  <div key={m.key} className="p-3 border border-[#E6E9F0] rounded-lg" data-testid={m.testId}>
                    <p className="text-xs text-[#8AA4BF]">{m.label}</p>
                    <p className="text-2xl font-light text-[#1F2A36]">
                      {r?.own_rank ? `${r.own_rank}.` : "—"}
                      <span className="text-sm text-[#8AA4BF]"> av {r?.count ?? "—"}</span>
                    </p>
                    <p className="text-[11px] text-[#4B647F]">
                      {m.fmt(r?.own_value)} · {m.note}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { rows: land.barnefattigdom.topp, title: "Mest barnefattigdom i landet", testId: "land-topp" },
                { rows: land.barnefattigdom.botn, title: "Minst barnefattigdom i landet", testId: "land-botn" },
              ].map((lista) => (
                <div key={lista.testId} data-testid={lista.testId}>
                  <p className="text-xs font-medium text-[#1F2A36] mb-2">{lista.title}</p>
                  <div className="space-y-1">
                    {lista.rows.map((r) => (
                      <div
                        key={r.kommune_code}
                        className={`flex items-center gap-2 text-xs ${r.kommune_code === kommuneCode ? "font-semibold text-[#103766]" : "text-[#4B647F]"}`}
                        data-testid={`${lista.testId}-${r.kommune_code}`}
                      >
                        <span className="w-8 text-right text-[#8AA4BF]">{r.rank}.</span>
                        <span className="flex-1 truncate">{r.name}</span>
                        <span className="tabular-nums">{p(r.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {land.barnefattigdom.own_rank &&
              !land.barnefattigdom.topp.concat(land.barnefattigdom.botn).some((r) => r.kommune_code === kommuneCode) && (
                <p className="text-xs text-[#103766] font-medium mt-3" data-testid="land-eigen-plass">
                  {kommuneName}: nr. {land.barnefattigdom.own_rank} av {land.barnefattigdom.count} med {p(land.barnefattigdom.own_value)} – landssnittet er{" "}
                  {p(land.land_snitt_andel)}
                </p>
              )}
          </div>
        )}

        <p className="text-xs text-[#8AA4BF]">
          Kjelder: SSB{" "}
          <a href="https://www.ssb.no/statbank/table/09114" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            09114 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (Gini og P90/P10) og{" "}
          <a href="https://www.ssb.no/statbank/table/12558" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12558 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (inntektsgrupper) og{" "}
          <a href="https://www.ssb.no/statbank/table/12944" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12944 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (barn i vedvarande låginntekt), KOSTRA{" "}
          <a href="https://www.ssb.no/statbank/table/12362" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12362 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (netto driftsutgifter) og{" "}
          <a href="https://www.ssb.no/statbank/table/07459" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            07459 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (talet på barn 0–17 år). Tala gjeld hushald og inntekt etter skatt; studenthushald er haldne utanom i SSB sine ulikskapsmål.
        </p>
      </CardContent>
    </Card>
  );
};

export default UlikskapSection;
